import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Stack,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Suspense, lazy, useState } from 'react';

import {
  REPORT_PERIODS,
  fetchCustomers,
  fetchDashboard,
  fetchLanes,
  type ReportPeriodValue,
} from '@/api/reports';
import { useAuth } from '@/features/auth/useAuth';
import { CustomerCharts } from '@/features/reporting/CustomerCharts';
import { KpiTile } from '@/features/reporting/KpiTile';
import { LaneTable } from '@/features/reporting/LaneTable';
import { TrendCharts } from '@/features/reporting/TrendCharts';
import { formatAge, formatCount, formatMoney, formatRate } from '@/features/reporting/formatting';

/**
 * The dashboard.
 *
 * THE MAP IS LAZY-LOADED. Leaflet and its stylesheet are around 150 kB that only matter on one tab
 * of one page, and every other screen in this application would otherwise pay for them on first
 * load. `React.lazy` keeps it in its own chunk fetched when the Routes tab is opened.
 *
 * THREE QUERIES, NOT ONE. The KPI tiles and trend charts arrive together because they are read
 * together and must not disagree; lanes and customers are fetched only when their tab is opened.
 * A dashboard that loads four hundred lanes to render a summary nobody scrolled to is slow for a
 * reason its user cannot see.
 */
const RouteMap = lazy(() =>
  import('@/features/reporting/RouteMap').then((m) => ({ default: m.RouteMap })),
);

export function DashboardPage() {
  const { user, activeOrganization } = useAuth();
  const [period, setPeriod] = useState<ReportPeriodValue>('12m');
  const [tab, setTab] = useState(0);

  const dashboard = useQuery({
    queryKey: ['reports', 'dashboard', period],
    queryFn: () => fetchDashboard(period),
  });

  const lanes = useQuery({
    queryKey: ['reports', 'lanes'],
    queryFn: () => fetchLanes(25),
    enabled: tab === 1,
  });

  const customers = useQuery({
    queryKey: ['reports', 'customers'],
    queryFn: () => fetchCustomers(15),
    enabled: tab === 2,
  });

  if (dashboard.isLoading) {
    return (
      <Stack alignItems="center" sx={{ py: 10 }}>
        <CircularProgress />
      </Stack>
    );
  }

  if (dashboard.isError || !dashboard.data) {
    return <Alert severity="error">Could not load the dashboard.</Alert>;
  }

  const { kpis, trend, meta } = dashboard.data;
  const currency = meta.currency;

  return (
    <Stack spacing={3}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ sm: 'flex-end' }}
        spacing={2}
      >
        <Stack spacing={0.5}>
          <Typography variant="h1">Welcome, {user?.name.split(' ')[0]}</Typography>
          <Typography variant="body2" color="text.secondary">
            {activeOrganization?.name} · reporting in {currency}
          </Typography>
        </Stack>

        <ToggleButtonGroup
          size="small"
          exclusive
          value={period}
          onChange={(_, value: ReportPeriodValue | null) => value && setPeriod(value)}
          aria-label="Reporting period"
        >
          {REPORT_PERIODS.map((p) => (
            <ToggleButton key={p.value} value={p.value}>
              {p.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Stack>

      {/* Staleness is stated, always. A dashboard that does not say how old it is, is one people
          stop trusting the first time a fresh win does not appear on it. */}
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="caption" color="text.secondary">
          Trends rebuilt {formatAge(meta.as_of)}. Tiles marked ● are live.
        </Typography>
      </Stack>

      {meta.stale_views.length > 0 && (
        <Alert severity="warning">
          {meta.stale_views.length} reporting view(s) failed to rebuild, so some figures below are
          older than they look: {meta.stale_views.join(', ')}.
        </Alert>
      )}

      {meta.unconverted_quotes > 0 && (
        <Alert severity="info">
          {meta.unconverted_quotes} amount(s) are missing from the money figures — no exchange rate
          into {currency} was available for their currency. Counts are unaffected.
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: {
            xs: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
            lg: 'repeat(5, 1fr)',
          },
        }}
      >
        <KpiTile
          label="Revenue won"
          value={formatMoney(kpis.won_value_minor, currency)}
          sample={`${formatCount(kpis.won_count)} quotes accepted`}
          hint={`Accepted quotes over the ${meta.period_label.toLowerCase()}.`}
          emphasis
        />
        <KpiTile
          label="Win rate"
          value={formatRate(kpis.win_rate_basis_points)}
          sample={`${formatCount(kpis.decided_count)} decided`}
          hint="Won ÷ decided. A quote that lapsed unanswered is not counted as a loss."
        />
        <KpiTile
          label="Avg margin"
          value={formatRate(kpis.avg_margin_basis_points)}
          sample={`over ${formatCount(kpis.margin_sample)} won quotes`}
          hint="Won business with a recorded cost. Quotes with no cost entered are excluded."
        />
        <KpiTile
          label="Open requests"
          value={formatCount(kpis.open_rfqs)}
          sample={`${formatCount(kpis.rfqs_today)} arrived today`}
          live
        />
        <KpiTile
          label="Awaiting sign-off"
          value={formatCount(kpis.approvals_outstanding)}
          sample={`${formatCount(kpis.quotes_expiring_soon)} quotes lapse within 7 days`}
          live
        />
      </Box>

      <Card variant="outlined">
        <Tabs value={tab} onChange={(_, v: number) => setTab(v)}>
          <Tab label="Trends" />
          <Tab label="Routes" />
          <Tab label="Customers" />
        </Tabs>
        <Divider />
        <CardContent>
          {tab === 0 && <TrendCharts points={trend} currency={currency} />}

          {tab === 1 && (
            <Stack spacing={2}>
              {lanes.isLoading && (
                <Stack alignItems="center" sx={{ py: 6 }}>
                  <CircularProgress size={24} />
                </Stack>
              )}
              {lanes.data && (
                <>
                  <Suspense
                    fallback={
                      <Stack alignItems="center" sx={{ py: 6 }}>
                        <CircularProgress size={24} />
                      </Stack>
                    }
                  >
                    <RouteMap lanes={lanes.data.data} currency={currency} />
                  </Suspense>
                  <LaneTable lanes={lanes.data.data} currency={currency} />
                </>
              )}
            </Stack>
          )}

          {tab === 2 && (
            <>
              {customers.isLoading && (
                <Stack alignItems="center" sx={{ py: 6 }}>
                  <CircularProgress size={24} />
                </Stack>
              )}
              {customers.data && (
                <CustomerCharts customers={customers.data.data} currency={currency} />
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Tooltip title="Rebuilt hourly by the reporting:refresh scheduler.">
        <Typography variant="caption" color="text.disabled">
          Revenue, win rate, margin and accuracy come from pre-aggregated views rebuilt every hour.
          Open requests and sign-off counts are read live.
        </Typography>
      </Tooltip>
    </Stack>
  );
}
